import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useMemo, useRef } from "react";
import { selectFlightAction } from "../../redux/slices/flight/selected";
import IconButton from "@mui/material/IconButton";
import {
  getIconComponentByType,
  getIconComponentByTypeWithProps,
} from "../../utils/assets";
import { formatDate, getStringDateFromDate } from "../../utils/formattings";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import Badge from "@mui/material/Badge";
import MuiButton from "@mui/material/Button";
import TextField from "../Common/TextField";
import Stack from "@mui/material/Stack";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import Box from "@mui/material/Box";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { styled } from "@mui/material";
import { measurementDeleteAction } from "redux/slices/measurement/delete";
import { resetCreateMeasureAction } from "redux/slices/measurement/create";
import {
  getMeasurementListAction,
  updateMeasureDataListAction,
  resetMeasurementFiltersAction
} from "redux/slices/measurement/list";
import { removeMeasurementLayers } from "utils/layers/mapUtils";

const IconButtonStyled = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'hasFlights',
})(({ theme, hasFlights, disabled }) => ({
  color: hasFlights && !disabled ? "#FFC500" : "#808080", // Yellow if flights available, gray otherwise
  "&:disabled": {
    color: "#808080",
    opacity: 0.5,
  },
  "&:hover": {
    backgroundColor: hasFlights && !disabled 
      ? "rgba(255, 197, 0, 0.1)" // Light yellow hover when active
      : "rgba(128, 128, 128, 0.1)", // Gray hover when inactive
  },
}));

const DateButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== 'customProp',
})(({ theme }) => ({
  backgroundColor: "#FFC500", // Yellow background
  color: "#000000", // Dark/black text
  minWidth: "120px",
  padding: "8px 16px",
  borderRadius: "8px",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 400,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#E6B300", // Slightly darker yellow on hover
    boxShadow: "none",
  },
}));

const FlightSelector = () => {
  const selectedFlight = useSelector((state) => state.flightSelected);
  const isPano = useSelector((state) => state.mapState.isPano);

  const flightListData = useSelector((state) => state.flightList.data);
  
  const flights = useMemo(() => {
    return flightListData.map(
      (item, idx) =>
        flightListData[flightListData.length - 1 - idx]
    );
  }, [flightListData]);
  
  // Create a stable key from flight IDs to use as dependency
  const flightsIdsKey = useMemo(() => {
    return flights.map(f => f.id || f.uuid || f.flown_date).join(',');
  }, [flightListData]);
  
  // Use refs to track previous values and prevent infinite loops
  const prevSelectedFlightIdRef = useRef(null);
  const prevFlightsIdsKeyRef = useRef('');
  
  const flownOnDates = useMemo(() => {
    return flights.map((flight) =>
      getStringDateFromDate(new Date(flight.flown_date))
    );
  }, [flightsIdsKey]);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [previousFlights, setPreviousFlights] = useState([]);
  const [nextFlights, setNextFlights] = useState([]);
  const dateButtonRef = useRef(null);
  const datePickerWrapperRef = useRef(null);
  const selectedItem = useSelector(
    (state) =>
      state.drawerState.menuItems.filter((item) => item.selected === true)[0]
  );

  const dispatch = useDispatch();
  const handleDateChange = (date) => {
    if (!date) return;
    const currentSelectedFlight = flights.filter(
      (flight) =>
        getStringDateFromDate(date) ===
        getStringDateFromDate(new Date(flight.flown_date))
    );
    if (currentSelectedFlight.length > 0) {
      dispatch(selectFlightAction({ flight: currentSelectedFlight[0] }));
    }
    setOpen(false);
  };

  useEffect(() => {
    if (open && dateButtonRef.current) {
      setAnchorEl(dateButtonRef.current);
    }
  }, [open]);

  useEffect(() => {
    if (!selectedFlight?.flown_date || flights.length === 0) return;
    
    // Check if we've already processed this combination
    const currentFlightId = selectedFlight?.id || selectedFlight?.uuid;
    
    if (
      prevSelectedFlightIdRef.current === currentFlightId &&
      prevFlightsIdsKeyRef.current === flightsIdsKey &&
      prevSelectedFlightIdRef.current !== null
    ) {
      return; // Skip if nothing has changed
    }
    
    // Recalculate dates inside effect to avoid dependency on array reference
    const dates = flights.map((flight) =>
      getStringDateFromDate(new Date(flight.flown_date))
    );
    
    const selectedDateString = getStringDateFromDate(new Date(selectedFlight.flown_date));
    const currentElementIndex = dates.findIndex(
      (date) => date === selectedDateString
    );
    
    if (currentElementIndex === -1) return;
    
    setPreviousFlights(flights.slice(0, currentElementIndex));
    setNextFlights(
      flights.slice(
        currentElementIndex + 1,
        currentElementIndex + flights.length
      )
    );
    
    // Update refs
    prevSelectedFlightIdRef.current = currentFlightId;
    prevFlightsIdsKeyRef.current = flightsIdsKey;
    
    // dispatch(getMeasurementListAction());
  }, [selectedFlight?.id, selectedFlight?.uuid, selectedFlight?.flown_date, flightsIdsKey, flights]);

  const updateMeasurements = () => {
    // dispatch(resetCreateMeasureAction());
    if(selectedItem?.name === 'Measure') {
      dispatch(getMeasurementListAction());
    }
}

  const handleNext = (e) => {    
    e.preventDefault();
    e.stopPropagation();
    if (nextFlights.length > 0) {
      dispatch(selectFlightAction({ flight: nextFlights[0] }));
      updateMeasurements();
    }
  };

  const handlePrev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (previousFlights.length > 0) {
      dispatch(
        selectFlightAction({
          flight: previousFlights[previousFlights.length - 1],
        })
      );
      updateMeasurements();
    }
  };

  return (
    Object.keys(selectedFlight).length !== 0 && (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ overflow: 'visible' }}>
        <IconButtonStyled
          disabled={isPano || previousFlights.length === 0}
          hasFlights={previousFlights.length > 0}
          onClick={handlePrev}
          size="small"
        >
          {getIconComponentByType("arrowBackIos")}
        </IconButtonStyled>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box ref={datePickerWrapperRef} sx={{ position: 'relative', display: 'inline-block' }}>
            <DateButton
              ref={dateButtonRef}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
                setOpen(true);
              }}
            >
              {formatDate(selectedFlight.flown_date, "yyyy-mm-dd")}
            </DateButton>
            <DesktopDatePicker
              open={open}
              onClose={() => {
                setOpen(false);
              }}
              value={new Date(selectedFlight.flown_date)}
              onChange={handleDateChange}
              slots={{
                textField: () => null,
              }}
              slotProps={{
                popper: {
                  open: open,
                  anchorEl: anchorEl,
                  placement: 'bottom-start',
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ],
                },
              }}
              shouldDisableDate={(day) => {
                return !flownOnDates.includes(getStringDateFromDate(day));
              }}
              renderDay={(date, selectedDate, pickersDayProps) => {
                const isSelected =
                  date &&
                  !!flownOnDates.find(
                    (flownDate) => flownDate === getStringDateFromDate(date)
                  );
                return (
                  <Box display="flex-inline">
                    <Badge
                      badgeContent={
                        isSelected
                          ? getIconComponentByTypeWithProps("flightIcon")({
                              color: "primary",
                            })
                          : null
                      }
                    >
                      <PickersDay {...pickersDayProps} />
                    </Badge>
                  </Box>
                );
              }}
            />
          </Box>
        </LocalizationProvider>
        <IconButtonStyled
          disabled={isPano || nextFlights.length === 0}
          hasFlights={nextFlights.length > 0}
          onClick={handleNext}
          size="small"
        >
          {getIconComponentByType("arrowForwardIos")}
        </IconButtonStyled>
      </Stack>
    )
  );
};

export default FlightSelector;
